import {
  DatasetObjectFragment,
  DatasetTableType,
  LayoutSummaryFragment,
  LoadDatasetObjectDocument,
  LoadDatasetObjectQuery,
  LoadDatasetObjectQueryVariables,
  LoadLayoutsSummaryDocument,
  LoadLayoutsSummaryQuery,
  LoadLayoutsSummaryQueryVariables,
} from '@warebee/frontend/data-access-api-graphql';
import {
  DatasetExtraField,
  DatasetExtraFieldSettings,
  DatasetObjectDataFieldSummary,
} from '@warebee/shared/common';
import {
  ActivityFeedQueryBuilderParams,
  ActualityEmployeeShifts,
  FeedDistinctAgents,
  FeedDistinctUoms,
  getActualityEmployeeShiftsQuery,
  getFeedDateRangeQuery,
  getFeedDistinctAgentsQuery,
  getFeedDistinctUomsQuery,
  getFeedItemsWithRanksQuery,
} from '@warebee/shared/dataset-queries';
import {
  ActivityEventProcessType,
  ActivityEventType,
} from '@warebee/shared/import-converter';
import { addHours, differenceInDays } from 'date-fns';
import _ from 'lodash';
import { atom, selector } from 'recoil';
import { persistAtom } from '../../common/recoil/persistAtom';
import { secureClient } from '../../GraphQLClient';
import { presentationDatasetExtraFieldsSettingsByType } from '../../presentationSettings/presentationSettings.state';
import { getProductCategory } from '../../simulation/store/abc/simulation.ABC.helper';
import { warehouseSelectedId } from '../../store/warehouse.state';
import {
  actualityExtraSettings,
  actualityQueryBuilderParams,
  actualityQueryReuseHashSources,
  actualitySelectedDocument,
} from './actuality.state';
import { ActualityActivityDatasetTypeKey } from './actuality.types';
import { executeDatasetQuery } from './feed.helper';
import {
  feedAnalysedJobDisplayOptions,
  feedDisplayOptions,
  FeedItemWithABCRank,
} from './feed.types';

const getKey = (postfix: string) => `warebee-activity-${postfix}`;

export const feedDatasetId = selector<string>({
  key: getKey('dataset-id'),
  get: ({ get }) => {
    return get(actualitySelectedDocument)?.activityFeedId;
  },
});

export const feedDatasetObject = selector<DatasetObjectFragment>({
  key: getKey('feed-dataset-object'),
  get: async ({ get }) => {
    const activityFeedId = get(feedDatasetId);
    if (_.isNil(activityFeedId)) return null;

    try {
      const response = await secureClient.query<
        LoadDatasetObjectQuery,
        LoadDatasetObjectQueryVariables
      >({
        query: LoadDatasetObjectDocument,
        variables: {
          id: activityFeedId,
        },
      });
      if (!_.isEmpty(response.errors)) {
        console.error('Cannot load Activity Feed document', response.errors);
        throw new Error('Cannot load Activity Feed document');
      }
      return response.data.datasetObject;
    } catch (ex) {
      console.error('Cannot load Activity Feed document', ex);
      throw new Error('Cannot load Activity Feed document');
    }
  },
});

export const feedDatasetExtraFields = selector<DatasetExtraField[]>({
  key: getKey('extra-dataset-fields'),
  get: ({ get }) => {
    const datasetFields = (get(feedDatasetObject)?.summary?.rawDataFields ??
      []) as DatasetObjectDataFieldSummary[];
    const datasetFieldsSettings = get(
      presentationDatasetExtraFieldsSettingsByType(
        ActualityActivityDatasetTypeKey,
      ),
    );
    const settingsMap = _.keyBy(
      datasetFieldsSettings?.fieldsSettings,
      s => s.name,
    );
    return _(datasetFields)
      .map(f => ({ ...f, ...settingsMap[f.name] }))
      .filter(f => f.enabled)
      .value();
  },
});

export const feedDatasetExtraFieldSettings = selector<
  DatasetExtraFieldSettings[]
>({
  key: getKey('dataset-extra-fields-settings'),
  get: ({ get }) => {
    const extraSettings = get(actualityExtraSettings);
    return _.find(
      extraSettings.datasetExtraFieldsSettings,
      f => f.datasetType === ActualityActivityDatasetTypeKey,
    )?.fieldsSettings as DatasetExtraFieldSettings[];
  },

  set: ({ get, set }, value: DatasetExtraFieldSettings[]) => {
    const extraSettings = get(actualityExtraSettings);
    const settings = _.filter(
      extraSettings.datasetExtraFieldsSettings,
      f => f.datasetType !== ActualityActivityDatasetTypeKey,
    );
    settings.push({
      datasetType: ActualityActivityDatasetTypeKey,
      fieldsSettings: value,
    });
    set(actualityExtraSettings, current => ({
      ...current,
      datasetExtraFieldsSettings: settings,
    }));
  },
});

export const feedLayoutId = selector<string>({
  key: getKey('layout-id'),
  get: ({ get }) => {
    return get(actualitySelectedDocument)?.layoutId;
  },
});

export const feedLayoutSummary = selector<LayoutSummaryFragment>({
  key: getKey('layout-summary'),
  get: async ({ get }) => {
    const layoutId = get(feedLayoutId);
    if (!layoutId) return null;

    try {
      const response = await secureClient.query<
        LoadLayoutsSummaryQuery,
        LoadLayoutsSummaryQueryVariables
      >({
        query: LoadLayoutsSummaryDocument,
        variables: {
          layoutId,
        },
      });

      if (!_.isEmpty(response.errors)) {
        console.error('Cannot load Layout Summary', response.errors);
        return null;
      }

      return response.data.layout.summary;
    } catch (ex) {
      console.error('Cannot load Layout Summary', ex);
      return null;
    }
  },
});

export const activityFeedSidebarMenuState = persistAtom<boolean>({
  key: 'show-menu-feed-main',
  default: false,
});

//#region Activity Feed Date Range
export const feedDateRangeAtom = atom<[Date, Date]>({
  key: getKey('date-range-atom'),
  default: null,
});

export const feedDateRange = selector<[Date, Date]>({
  key: getKey('date-range'),
  get: async ({ get }) => {
    const warehouseId = get(warehouseSelectedId);
    const datasetId = get(feedDatasetId);
    const setted = get(feedDateRangeAtom);
    if (!_.isNil(setted)) return setted;
    if (_.isNil(datasetId)) return [null, null];

    const compiledQuery = getFeedDateRangeQuery(datasetId).compile();

    try {
      const [result] = await executeDatasetQuery({
        warehouseId,
        compiledQuery,
        comment: '[feed] Date Range',
        reuseHashSource: [
          {
            tableType: DatasetTableType.ACTIVITY_FEED,
            id: datasetId,
          },
        ],
      });

      if (
        result &&
        result.length > 0 &&
        result[0].minTime &&
        result[0].maxTime
      ) {
        return [result[0].minTime, result[0].maxTime];
      } else {
        console.warn(
          '[feedDateRange] Invalid or empty result, returning default values',
        );
        return [null, null];
      }
    } catch (ex) {
      console.error('[feedDateRange] Exception occurred:', ex);
      return [null, null];
    }
  },
});

export const feedSelectedDateRange = atom<[Date, Date]>({
  key: getKey('selected-range'),
  default: null,
});

export const feedEffectiveDateRange = selector<[Date, Date]>({
  key: getKey('effective-date-range'),
  get: async ({ get }) => {
    const [min, max] = get(feedDateRange) ?? [];
    const [calendarFrom, calendarTo] = get(feedSelectedDateRange) ?? [];
    const from = calendarFrom ?? addHours(max, -1);
    const to = calendarTo ?? max;
    return [from, to];
  },
});

export const feedIsDateRangeWithinDay = selector<boolean>({
  key: getKey('feed-is-date-range-within-day'),
  get: ({ get }) => {
    return differenceInDays(...get(feedEffectiveDateRange)) <= 1;
  },
});
//#endregion Activity Feed Date Range

export const feedDisabledAgentTypes = persistAtom<string[]>({
  key: getKey('selected-agent-types'),
  default: [],
});

export const feedDisabledProcessTypes = persistAtom<ActivityEventProcessType[]>(
  {
    key: getKey('selected-process-types'),
    default: [],
  },
);

export const feedInformationalEventTypes = persistAtom<ActivityEventType[]>({
  key: getKey('informational-event-types'),
  default: [ActivityEventType.JOB_START, ActivityEventType.JOB_END],
});

export const feedSelectedPickListId = atom<string>({
  key: getKey('selected-pick-list'),
  default: null,
});

export const feedHoveredPickListId = atom<string>({
  key: getKey('hovered-pick-list'),
  default: null,
});

export const feedQueryBuilderIsReady = selector<boolean>({
  key: getKey('query-builder-is-ready'),
  get: ({ get }) => {
    const datasetId = get(feedDatasetId);
    const [from, to] = get(feedEffectiveDateRange);
    return !_.isNil(datasetId) && !_.isNil(from) && !_.isNil(to);
  },
});

export const feedQueryBuilderParams = selector<ActivityFeedQueryBuilderParams>({
  key: getKey('query-builder-params'),
  get: ({ get }) => {
    const datasetId = get(feedDatasetId);
    const processTypes = get(feedDisabledProcessTypes);
    const agentTypes = get(feedDisabledAgentTypes);
    const informationalEventTypes = get(feedInformationalEventTypes);
    const extraFields = get(feedDatasetExtraFields);
    const params: ActivityFeedQueryBuilderParams = {
      datasetId,
      disabledProcessTypes: processTypes,
      disabledAgentTypes: agentTypes,
      informationalEventTypes,
      extraFields,
    };
    return params;
  },
});

export const feedDistinctAgents = selector<FeedDistinctAgents>({
  key: getKey('distinct-agents'),
  get: async ({ get }) => {
    if (!get(feedQueryBuilderIsReady)) return null;
    const datasetId = get(feedDatasetId);
    const warehouseId = get(warehouseSelectedId);
    const compiledQuery = getFeedDistinctAgentsQuery(datasetId).compile();
    const [result] = await executeDatasetQuery({
      warehouseId,
      compiledQuery,
      comment: '[feed] Distinct Agents',
      reuseHashSource: [
        {
          tableType: DatasetTableType.ACTIVITY_FEED,
          id: datasetId,
        },
      ],
    });
    return result;
  },
});

export const feedDistinctUoms = selector<FeedDistinctUoms>({
  key: getKey('distinct-uoms'),
  get: async ({ get }) => {
    const datasetId = get(feedDatasetId);
    const warehouseId = get(warehouseSelectedId);
    const compiledQuery = getFeedDistinctUomsQuery(datasetId).compile();
    const [result] = await executeDatasetQuery({
      warehouseId,
      compiledQuery,
      comment: '[feed] Distinct Uoms ',
      reuseHashSource: [
        {
          tableType: DatasetTableType.ACTIVITY_FEED,
          id: datasetId,
        },
      ],
    });
    return result;
  },
});

export const feedAgentShifts = selector<ActualityEmployeeShifts>({
  key: getKey('agent-shifts'),
  get: async ({ get }) => {
    if (!get(feedQueryBuilderIsReady)) return null;
    const warehouseId = get(warehouseSelectedId);
    const compiledQuery = getActualityEmployeeShiftsQuery(
      get(actualityQueryBuilderParams),
    ).compile();
    const [result] = await executeDatasetQuery({
      warehouseId,
      compiledQuery,
      comment: '[feed] Agent shifts ',
      reuseHashSource: get(actualityQueryReuseHashSources),
    });
    return result;
  },
});

export const feedItemsWithRank = selector<FeedItemWithABCRank[]>({
  key: getKey('items-with-rank'),
  get: async ({ get }) => {
    if (!get(feedQueryBuilderIsReady)) return null;
    const warehouseId = get(warehouseSelectedId);
    const compiledQuery = getFeedItemsWithRanksQuery(
      get(actualityQueryBuilderParams),
    ).compile();

    const [result] = await executeDatasetQuery({
      warehouseId,
      compiledQuery,
      comment: '[feed] Items With Rank',
      reuseHashSource: get(actualityQueryReuseHashSources),
    });

    return _.map(result, item => {
      const withAbc: FeedItemWithABCRank = {
        ...item,
        category: getProductCategory(item.cumulativePercentRank)?.key,
      };
      return withAbc;
    });
  },
});

export const feedItemsWithRankMap = selector<
  Record<string, FeedItemWithABCRank>
>({
  key: getKey('items-with-rank-map'),
  get: async ({ get }) => {
    const itemsWithRank = get(feedItemsWithRank);
    return _.keyBy(itemsWithRank, 'skuKey');
  },
});

export type FeedDisplayOption = (typeof feedDisplayOptions)[number]['value'];

export const feedDisplayOptionAtom = persistAtom<FeedDisplayOption>({
  key: getKey('feed-display-option'),
  default: 'all',
});

export type FeedAnalysedJobDisplayOptions =
  (typeof feedAnalysedJobDisplayOptions)[number]['value'];

export const feedAnalysedJobDisplayOptionAtom =
  persistAtom<FeedAnalysedJobDisplayOptions>({
    key: getKey('feed-analysed-job-display-option'),
    default: 'all',
  });
